import { Global, useTheme } from '@emotion/react';
import type { BoxProps, CenterProps } from '@hover/blueprint';
import {
  Box,
  Center,
  Drawer,
  Heading,
  IconButton,
  Modal,
  VStack,
  motion,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@hover/blueprint';
import { DrawerContent, ModalContent } from '@hover/blueprint/chakra';
import { iX } from '@hover/icons';
import React, { useEffect, useMemo, useState } from 'react';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import type { HomeownerAppTrackingData } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { Button } from 'src/shared/components';
import type { Job } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { moduleViewed } from 'src/shared/telemetry';

import { useAttachScanToProjectHandler } from '../../hooks/useAttachScanToProjectHandler';

import { ScanImageContainer } from './ScanImageContainer';

const MAX_SCANS_IMAGES = 3;

const MotionModalContent = motion(ModalContent);

export interface ScanImagesDetails {
  imageUrl: string;
  location?: string;
}
interface ScanPopUpProps {
  scans: Job[];
  projectId: string;
  onDisplayed: () => void;
}

interface ScanPopUpImagesProps extends CenterProps {
  scanImagesDetails: ScanImagesDetails[];
}

interface ScanPopUpCTAProps extends BoxProps {
  onCancel: () => void;
  onSubmit: () => void;
  trackingData: HomeownerAppTrackingData;
}

const filterScans = (scans: Job[]) => {
  const scansDetail: ScanImagesDetails[] = [];

  scans.forEach(scan => {
    if (scansDetail.length >= MAX_SCANS_IMAGES) return;
    if (scan.bestFrontImage && scan.bestFrontImage.imageMediumUrl) {
      scansDetail.push({
        imageUrl: scan.bestFrontImage.imageMediumUrl,
        location: scan.locationLine1 ?? undefined,
      });
    }
    scan.images?.forEach(image => {
      if (image.imageUrl) {
        scansDetail.push({
          imageUrl: image.imageUrl,
          location: scan.locationLine1 ?? undefined,
        });
      }
    });
  });

  return scansDetail;
};

const ScanPopUpCTA = ({
  onCancel,
  onSubmit,
  trackingData,
  ...props
}: ScanPopUpCTAProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  return (
    <Box p={400} {...props}>
      <VStack px={{ base: 600, tablet: 900 }}>
        <Heading my={500} textAlign="center">
          {t('scan.header')}
        </Heading>
        <Button
          h={500}
          onClick={onSubmit}
          optionalTrackingProperties={{
            ...trackingData,
            track_id: 'add-scan-now-module-button',
            button_location: 'Add scan now module',
          }}
          primaryCallToAction
          width="100%"
        >
          {t('scan.submit')}
        </Button>
        <Button h={500} onClick={onCancel} variant="ghost" width="100%">
          {t('scan.cancel')}
        </Button>
      </VStack>
    </Box>
  );
};

const ScanPopUpImages = ({
  scanImagesDetails,
  ...props
}: ScanPopUpImagesProps) => (
  <Center backgroundColor="primary.100" {...props}>
    <ScanImageContainer
      scanImages={scanImagesDetails.slice(0, MAX_SCANS_IMAGES)}
    />
  </Center>
);

const ScanPopUp = ({ scans, projectId, onDisplayed }: ScanPopUpProps) => {
  const { radii } = useTheme();
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const { trackingData } = useHomeownerAppContext();

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const toast = useToast({ type: 'error', position: 'top' });
  const [scanImagesDetails, setScanImagesDetails] = useState<
    ScanImagesDetails[]
  >([]);
  const [attachScanToProjectHandler] = useAttachScanToProjectHandler(
    projectId,
    scans[0],
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const scanTrackingData = useMemo(
    () => ({ ...trackingData, project_id: Number(projectId) }),
    [trackingData, projectId],
  );

  useEffect(() => {
    if (isOpen) {
      moduleViewed({
        ...scanTrackingData,
        track_id: 'add-scan-now-popup',
      });
    }
  }, [scanTrackingData, isOpen]);

  useEffect(() => {
    if (scans.length) {
      setScanImagesDetails(filterScans(scans));
      onOpen();
    }

    return onDisplayed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!scans.length) return null;

  const hideChakraModalContentCSS = `
    .chakra-modal__content{
      background: transparent !important;
      box-shadow: none !important;
    }
  `;

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await attachScanToProjectHandler();
    } catch {
      toast({ props: { message: t('projects.scan.attachError') } });
    }
    onClose();
  };

  return (
    <>
      {!isMobile ? (
        <>
          <Global styles={hideChakraModalContentCSS} />
          <Modal
            isCentered
            isClosable={false}
            isOpen={isOpen}
            onClose={onClose}
          >
            <MotionModalContent
              animate={{ opacity: 1, scale: 1 }}
              flexDirection="row"
              initial={{ opacity: 0, scale: 0.5 }}
              padding="0 !important"
              transition={{
                duration: 0.8,
                delay: 0.5,
                ease: [0, 0.71, 0.2, 1.01],
              }}
              width="886px"
            >
              <IconButton
                icon={iX}
                label="Close"
                onClick={onClose}
                position="absolute"
                right={20}
                top={20}
                variant="ghost"
              />
              <ScanPopUpCTA
                backgroundColor="neutral.0"
                maxW="50%"
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                pl={600}
                py={900}
                style={{ borderRadius: `${radii[300]} 0 0 ${radii[300]}` }}
                trackingData={scanTrackingData}
                width="50%"
              />
              <ScanPopUpImages
                maxWidth="50%"
                py={950}
                scanImagesDetails={scanImagesDetails}
                style={{ borderRadius: `0 ${radii[300]} ${radii[300]} 0` }}
                width="50%"
              />
            </MotionModalContent>
          </Modal>
        </>
      ) : (
        <Drawer isClosable isOpen={isOpen} onClose={onClose} placement="bottom">
          <DrawerContent backgroundColor="primary.100" padding="0 !important">
            <IconButton
              icon={iX}
              label="Close"
              onClick={handleCancel}
              position="absolute"
              right={20}
              top={20}
              variant="ghost"
            />
            <ScanPopUpImages pt={950} scanImagesDetails={scanImagesDetails} />
            <ScanPopUpCTA
              onCancel={handleCancel}
              onSubmit={handleSubmit}
              pb={900}
              trackingData={scanTrackingData}
            />
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
};

export { ScanPopUp };
